import React, { useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Dialog, DialogProps, ElevatedButton, TextButton, TextInput } from '@producer/ui-kit';

import { createFormDiff } from '../../../core/services/form.service';

import styles from './contact-info.modal.module.scss';
type ContactInfoModalProps = DialogProps & {
  onSubmit: (values: FormValues) => Promise<void>;
  defaultValues?: FormValues;
  title: string;
  onClose: () => void;
};

type FormValues = {
  name?: string;

  phone?: string;

  address?: {
    line1?: string;

    line2?: string;

    postalCode?: string;

    city?: string;

    country?: string;
  };
};

export const ContactInfoModal: React.FC<ContactInfoModalProps> = ({
  onClose,
  defaultValues,
  title,
  ...props
}) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);

  const schema = useMemo(
    () =>
      yup.object().shape({
        name: yup.string().max(32, t('common:fieldMax', { max: 32 })),
        phone: yup.string().max(16, t('common:fieldMax', { max: 16 })),
        address: yup.object().shape({
          line1: yup.string(),
          line2: yup.string(),
          postalCode: yup.string().max(12, t('common:fieldMax', { max: 12 })),
          city: yup.string(),
          country: yup.string(),
        }),
      }),
    [t],
  );

  const {
    register,
    handleSubmit,
    formState: { errors, dirtyFields },
  } = useForm<FormValues>({
    mode: 'onSubmit',
    resolver: yupResolver(schema),
    defaultValues: defaultValues || {
      name: '',
      phone: '',
      address: {
        line1: '',
        line2: '',
        postalCode: '',
        city: '',
        country: '',
      },
    },
  });

  const onSubmit = async (values: FormValues) => {
    if (isLoading) {
      return;
    }

    const diffs = createFormDiff(values, dirtyFields);

    setIsLoading(true);

    await props.onSubmit(diffs);

    onClose();
  };

  return (
    <Dialog
      title={title}
      onClose={onClose}
      actions={[
        <TextButton key={'cancel'} onClick={onClose} text={'Cancel'} />,
        <ElevatedButton
          key="save"
          text={'Save'}
          onClick={handleSubmit(onSubmit)}
          isLoading={isLoading}
        />,
      ]}
    >
      <div className={styles['content']}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <TextInput
            label={`${title} name`}
            {...register('name')}
            errorMessage={errors.name?.message}
          />

          <div className={styles['separator']} />

          <div className={styles['grid']}>
            <div style={{ gridColumn: 'span 3' }}>
              <TextInput label="Address" {...register('address.line1')} />
            </div>

            <TextInput
              label="Postal Code"
              {...register('address.postalCode')}
              errorMessage={errors.address?.postalCode?.message}
            />

            <div style={{ gridColumn: 'span 2' }}>
              <TextInput label="City" {...register('address.city')} />
            </div>

            <div style={{ gridColumn: 'span 3' }}>
              <TextInput label="Country" {...register('address.country')} />
            </div>
          </div>

          <div className={styles['separator']} />

          <TextInput label="Phone" {...register('phone')} errorMessage={errors.phone?.message} />
        </form>
      </div>
    </Dialog>
  );
};
